import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  ArrowLeft, Award, Calendar, Building2, MapPin, Globe,
  FileText, Image as ImageIcon, Link as LinkIcon, CheckCircle,
  Clock, Trophy, XCircle, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

export const SubmissionDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubmission();
  }, [id]);

  const fetchSubmission = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/submissions/${id}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSubmission(data);
      } else if (response.status === 404) {
        toast.error('Başvuru bulunamadı');
        navigate('/dashboard');
      } else if (response.status === 403) {
        toast.error('Bu başvuruyu görme yetkiniz yok');
        navigate('/dashboard');
      } else {
        toast.error('Başvuru yüklenirken hata oluştu');
      }
    } catch (error) {
      console.error('Error fetching submission:', error);
      toast.error('Bağlantı hatası');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Submitted': 'bg-gray-100 text-gray-800 border-gray-300',
      'Under Review': 'bg-blue-100 text-blue-800 border-blue-300',
      'Winner': 'bg-green-100 text-green-800 border-green-300',
      'Rejected': 'bg-red-100 text-red-800 border-red-300'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'Submitted': <FileText className="h-4 w-4" />,
      'Under Review': <Clock className="h-4 w-4" />,
      'Winner': <Trophy className="h-4 w-4" />,
      'Rejected': <XCircle className="h-4 w-4" />
    };
    return icons[status] || <FileText className="h-4 w-4" />;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin mx-auto text-orange-500" />
          <p className="text-gray-600">Başvuru yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!submission) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 max-w-5xl">
        {/* Header */}
        <div className="mb-8">
          <Link to="/dashboard">
            <Button variant="outline" className="mb-4" data-testid="back-to-dashboard">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Dashboard'a Dön
            </Button>
          </Link>
          
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2" data-testid="submission-title">
                {submission.category}
              </h1>
              <p className="text-gray-600 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Başvuru Tarihi: {formatDate(submission.submitted_at)}
              </p>
            </div>
            
            <Badge 
              className={`${getStatusColor(submission.status)} flex items-center gap-1 text-base px-4 py-2`}
              data-testid="submission-status"
            >
              {getStatusIcon(submission.status)}
              {submission.status}
            </Badge>
          </div>
        </div>

        {/* Basic Information */}
        <Card className="p-6 mb-6" data-testid="basic-info-section">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <Award className="h-6 w-6 text-orange-500" />
            Başvuru Bilgileri
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500">Ad Soyad</p>
                <p className="font-medium">
                  {submission.basic_info.first_name} {submission.basic_info.last_name}
                </p>
              </div>
              
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium">{submission.basic_info.email}</p>
              </div>
              
              {submission.basic_info.phone && (
                <div>
                  <p className="text-sm text-gray-500">Telefon</p>
                  <p className="font-medium">{submission.basic_info.phone}</p>
                </div>
              )}
            </div>
            
            <div className="space-y-4">
              {submission.basic_info.company && (
                <div className="flex items-start gap-2">
                  <Building2 className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Şirket</p>
                    <p className="font-medium">{submission.basic_info.company}</p>
                  </div>
                </div>
              )}
              
              {submission.basic_info.country && (
                <div className="flex items-start gap-2">
                  <Globe className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Ülke</p>
                    <p className="font-medium">{submission.basic_info.country}</p>
                  </div>
                </div>
              )}
              
              {submission.basic_info.city && (
                <div className="flex items-start gap-2">
                  <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Şehir</p>
                    <p className="font-medium">{submission.basic_info.city}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Category Specific Data */}
        <Card className="p-6" data-testid="category-data-section">
          <h2 className="text-2xl font-bold mb-4">Kategori Detayları</h2>
          
          <div className="space-y-6">
            {Object.entries(submission.category_specific_data).map(([key, value]) => {
              // Skip empty values
              if (!value || (Array.isArray(value) && value.length === 0)) {
                return null;
              }

              // Format field name
              const fieldName = key
                .split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');

              // Handle different data types
              if (Array.isArray(value) && value.length > 0) {
                // Photos array
                return (
                  <div key={key}>
                    <p className="font-semibold mb-2 flex items-center gap-2">
                      <ImageIcon className="h-5 w-5 text-orange-500" />
                      {fieldName}
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {value.map((photo, index) => (
                        <div key={index} className="aspect-square rounded-lg overflow-hidden border">
                          <img
                            src={photo}
                            alt={`Photo ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }

              if (typeof value === 'string' && value.startsWith('http')) {
                // URL field
                return (
                  <div key={key}>
                    <p className="font-semibold mb-2 flex items-center gap-2">
                      <LinkIcon className="h-5 w-5 text-orange-500" />
                      {fieldName}
                    </p>
                    <a
                      href={value}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline break-all"
                    >
                      {value}
                    </a>
                  </div>
                );
              }

              if (typeof value === 'string' && value.length > 100) {
                // Long text field
                return (
                  <div key={key}>
                    <p className="font-semibold mb-2">{fieldName}</p>
                    <p className="text-gray-700 whitespace-pre-wrap">{value}</p>
                  </div>
                );
              }

              // Short text or other fields
              return (
                <div key={key}>
                  <p className="font-semibold mb-1">{fieldName}</p>
                  <p className="text-gray-700">{value.toString()}</p>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Actions */}
        <div className="mt-8 flex justify-center">
          <Link to="/dashboard">
            <Button variant="outline" size="lg">
              Dashboard'a Dön
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SubmissionDetailsPage;
